import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, concat, finalize, tap } from 'rxjs';
import { ReservationService } from '../../../services/reservation.service';
import { RestaurantService } from '../../../services/restaurant.service';
import { ReservationMode, ReservationSettings } from '../../../models/reservation';
import { DateExceptionsComponent } from '../../controls/date-exceptions/date-exceptions.component';
import {
  DateException,
  HoursRange,
  ServingHoursDoc,
  ServingHoursGroup,
  WEEK_DAYS,
  DAY_MINUTES,
  dayBefore,
  hoursSpan,
  groupByRanges,
  groupsOfType,
  rangesForDay,
  DEFAULT_BOOKING_RANGE,
  bookingDayError,
  to12h,
} from '../../../utils/serving-hours.util';
import { TourComponent, TourStep } from '../../controls/fnf-tour/tour.component';

// Moved to serving-hours.util for the shared closed-dates list (FNF-1612).
export { bookingDayError, to12h };

/**
 * Most reminders one booking may get. Each one is another text, and a guest
 * who replies STOP also stops getting "table ready" and booking texts
 * (FNF-1177).
 */
export const MAX_REMINDERS = 2;

/**
 * Why one day's valid booking hours fall outside its opening hours, or null
 * (FNF-1515). Each range must fit one opening range of the day, or the part
 * of the day before's that runs past midnight, as reservation-service reads
 * them: Sun 00:30–02:00 fits Sat 21:00–04:00.
 */
export function outsideOpeningHoursError(
  ranges: HoursRange[],
  opening: HoursRange[],
  openingDayBefore: HoursRange[],
): string | null {
  const windows = [
    ...opening.map(hoursSpan),
    ...openingDayBefore
      .map(hoursSpan)
      .map(([start, end]): [number, number] => [start - DAY_MINUTES, end - DAY_MINUTES]),
  ];
  if (!windows.some(([, end]) => end > 0)) {
    return 'The restaurant is closed this day. Switch the day off, or set its opening hours on Restaurant Profile.';
  }
  const fits = ranges
    .map(hoursSpan)
    .every(([start, end]) => windows.some(([wStart, wEnd]) => wStart <= start && end <= wEnd));
  return fits ? null : 'Bookings must fall inside the opening hours.';
}

/**
 * Custom booking hours need at least one day. With none, reservation-service
 * would fall back to the opening hours, which is not what the owner chose.
 */
function bookingHoursValidator(
  dayError: (day: string, ranges: HoursRange[]) => string | null,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value as {
      custom: boolean;
      days: { day: string; open: boolean; ranges: HoursRange[] }[];
    };
    if (!v.custom) {
      return null;
    }
    const open = v.days.filter((d) => d.open);
    if (!open.length) {
      return { noDays: true };
    }
    return open.some((d) => dayError(d.day, d.ranges)) ? { badDay: true } : null;
  };
}

interface ModeOption {
  value: ReservationMode;
  title: string;
  icon: string;
  description: string;
}

/**
 * Restaurant → Reservation settings. Stores the restaurant's reservation
 * mode and policies (fnf-docs → Reservation Platform → Settings) through
 * `PUT /v1/restaurants/settings/reservation`, with optimistic locking on `version`.
 */
@Component({
  selector: 'app-reservation-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatTooltipModule,
    TourComponent,
    DateExceptionsComponent,
  ],
  templateUrl: './reservation-settings.component.html',
  styleUrl: './reservation-settings.component.css',
})
export class ReservationSettingsComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly reservationService = inject(ReservationService);
  private readonly toastr = inject(ToastrService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly restaurantService = inject(RestaurantService);

  readonly modes: ModeOption[] = [
    {
      value: 'off',
      title: 'Off',
      icon: 'bx-power-off',
      description: 'No online bookings. The Reservations tab is hidden for staff.',
    },
    {
      value: 'timed',
      title: 'Timed reservations',
      icon: 'bx-time-five',
      description: 'Guests pick a time slot; tables, turn times and pacing decide availability.',
    },
    {
      value: 'waitlist',
      title: 'Waitlist only',
      icon: 'bx-hourglass',
      description: 'Walk-ins join a live queue and get a text when their table is ready.',
    },
    {
      value: 'request',
      title: 'Request to book',
      icon: 'bx-message-square-check',
      description: 'Every booking arrives as a request you accept or decline within a window.',
    },
    {
      value: 'hybrid',
      title: 'Timed + waitlist',
      icon: 'bx-layer',
      description: 'Reservations ahead of time plus a same-day waitlist sharing the same tables.',
    },
  ];
  readonly reminderOptions = [
    { minutes: 2880, label: '2 days before' },
    { minutes: 1440, label: 'Day before' },
    { minutes: 240, label: '4 hours before' },
    { minutes: 120, label: '2 hours before' },
    { minutes: 60, label: '1 hour before' },
  ];
  readonly maxReminders = MAX_REMINDERS;

  form!: FormGroup;
  /**
   * Booking hours (FNF-1181) live in the restaurant's serving hours, not in
   * the reservation settings, so they have their own form and their own save.
   */
  hoursForm: FormGroup | null = null;
  hoursState: 'loading' | 'ready' | 'failed' = 'loading';
  /** Opening hours, shown next to each day and copied in as a starting point. */
  private storeGroups: ServingHoursGroup[] = [];

  /**
   * Closed dates and special hours (FNF-1181), for app-date-exceptions, which
   * saves them one at a time, not with the Save settings button.
   */
  dateExceptions: DateException[] = [];
  displayLoading = true;
  saving = false;
  private version = 1;

  // Guided tour of this page. Auto-starts once per browser; the ? replays it.
  tourOpen = false;
  tourSteps: TourStep[] = [];
  private static readonly TOUR_SEEN_KEY = 'fnf.reservationSettings.tourSeen';

  ngOnInit(): void {
    this.loadBookingHours();
    this.reservationService.getSettings().subscribe({
      next: (settings) => {
        this.version = settings.version;
        this.form = this.build(settings);
        this.displayLoading = false;
        if (!this.tourSeen()) {
          // Wait for the form to paint so the tour can find its anchors.
          setTimeout(() => this.startTour(), 900);
        }
      },
      error: () => {
        this.toastr.error('Unable to load reservation settings.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  private build(s: ReservationSettings): FormGroup {
    return this.fb.group({
      mode: [s.mode, Validators.required],
      slotIntervalMinutes: [
        s.slotIntervalMinutes,
        [Validators.required, Validators.min(5), Validators.max(60)],
      ],
      turnTimes: this.fb.array(
        s.turnTimes.map((t) =>
          this.fb.group({
            maxParty: [t.maxParty, [Validators.required, Validators.min(1), Validators.max(99)]],
            minutes: [t.minutes, [Validators.required, Validators.min(15), Validators.max(360)]],
          }),
        ),
      ),
      pacing: this.fb.group({
        maxCoversPerInterval: [
          s.pacing.maxCoversPerInterval,
          [Validators.required, Validators.min(1), Validators.max(500)],
        ],
      }),
      party: this.fb.group({
        min: [s.party.min, [Validators.required, Validators.min(1)]],
        max: [s.party.max, [Validators.required, Validators.min(1), Validators.max(100)]],
        autoConfirmUpTo: [
          s.party.autoConfirmUpTo,
          [Validators.required, Validators.min(0), Validators.max(100)],
        ],
      }),
      window: this.fb.group({
        minNoticeMinutes: [
          s.window.minNoticeMinutes,
          [Validators.required, Validators.min(0), Validators.max(10080)],
        ],
        maxDaysAhead: [
          s.window.maxDaysAhead,
          [Validators.required, Validators.min(1), Validators.max(365)],
        ],
      }),
      policy: this.fb.group({
        depositCents: [{ value: s.policy.depositCents, disabled: true }],
        cancelCutoffMinutes: [
          s.policy.cancelCutoffMinutes,
          [Validators.required, Validators.min(0), Validators.max(10080)],
        ],
        noShowGraceMinutes: [
          s.policy.noShowGraceMinutes,
          [Validators.required, Validators.min(0), Validators.max(120)],
        ],
      }),
      requestExpiryMinutes: [
        s.requestExpiryMinutes,
        [Validators.required, Validators.min(15), Validators.max(10080)],
      ],
      waitlist: this.fb.group({
        quoteStrategy: [s.waitlist.quoteStrategy],
        notifyTimeoutMinutes: [
          s.waitlist.notifyTimeoutMinutes,
          [Validators.required, Validators.min(3), Validators.max(60)],
        ],
      }),
      channels: this.fb.group({
        google: this.fb.group({ enabled: [{ value: s.channels.google.enabled, disabled: true }] }),
        opentable: this.fb.group({
          enabled: [{ value: s.channels.opentable.enabled, disabled: true }],
          merchantId: [s.channels.opentable.merchantId],
        }),
      }),
      notifications: this.fb.group({
        sms: [s.notifications.sms],
        email: [s.notifications.email],
        // Coming soon: guests are only told by SMS and email today (FNF-1129 /
        // FNF-1130). Disabled like the channels; getRawValue() still saves it.
        push: [{ value: s.notifications.push, disabled: true }],
        reminderMinutesBefore: [s.notifications.reminderMinutesBefore],
      }),
    });
  }

  // ------------------------------------------------------- booking hours

  private loadBookingHours(): void {
    this.restaurantService.getRestaurantServingHours().subscribe((res: any) => {
      // The service turns a failed request into the error object itself.
      if (!res || res instanceof HttpErrorResponse || (!res.data && res.error)) {
        this.hoursState = 'failed';
        return;
      }
      this.hoursForm = this.buildHoursForm(res.data as ServingHoursDoc | null);
      this.dateExceptions = (res.data as ServingHoursDoc | null)?.date_exceptions ?? [];
      this.hoursState = 'ready';
    });
  }

  // ------------------------------------------- closed dates & special hours

  /** Special hours shape bookings, so they are offered only in timed modes. */
  get offersSpecialHours(): boolean {
    return this.showsTimed;
  }

  private buildHoursForm(doc: ServingHoursDoc | null): FormGroup {
    const groups = doc?.serving_hours ?? [];
    this.storeGroups = groupsOfType(groups, 'store_hours');
    const booking = groupsOfType(groups, 'reservation_hours');
    // Not custom yet: start each day from its opening hours, so switching to
    // custom shows the current behaviour to trim from.
    const source = booking.length ? booking : this.storeGroups;
    return this.fb.group(
      {
        custom: [booking.length > 0],
        days: this.fb.array(
          WEEK_DAYS.map((day) => {
            const ranges = rangesForDay(source, day);
            // A day switched on later starts from its own opening hours.
            const fallback = rangesForDay(this.storeGroups, day);
            return this.fb.group({
              day: [day],
              open: [ranges.length > 0],
              ranges: this.fb.array(
                (ranges.length ? ranges : fallback.length ? fallback : [DEFAULT_BOOKING_RANGE]).map(
                  (r) => this.rangeGroup(r),
                ),
              ),
            });
          }),
        ),
      },
      { validators: bookingHoursValidator((day, ranges) => this.bookingError(day, ranges)) },
    );
  }

  /**
   * A day's booking hours on their own, then against its opening hours.
   * With no opening hours anywhere, booking hours are all the restaurant has.
   */
  private bookingError(day: string, ranges: HoursRange[]): string | null {
    return (
      bookingDayError(ranges) ??
      (this.hasOpeningHours
        ? outsideOpeningHoursError(
            ranges,
            rangesForDay(this.storeGroups, day),
            rangesForDay(this.storeGroups, dayBefore(day)),
          )
        : null)
    );
  }

  private rangeGroup(r: HoursRange): FormGroup {
    return this.fb.group({
      start_time: [r.start_time, Validators.required],
      end_time: [r.end_time, Validators.required],
    });
  }

  get customHours(): boolean {
    return !!this.hoursForm?.get('custom')?.value;
  }

  get hoursDays(): FormArray {
    return this.hoursForm!.get('days') as FormArray;
  }

  rangesOf(dayIndex: number): FormArray {
    return this.hoursDays.at(dayIndex).get('ranges') as FormArray;
  }

  get hasOpeningHours(): boolean {
    return this.storeGroups.some((g) => (g.hours ?? []).some((h) => h.start_time && h.end_time));
  }

  /** "11:00 AM – 11:00 PM", several joined by commas, or "Closed". */
  openingHoursLabel(day: string): string {
    const ranges = rangesForDay(this.storeGroups, day);
    return ranges.length
      ? ranges.map((r) => `${to12h(r.start_time)} – ${to12h(r.end_time)}`).join(', ')
      : 'Closed';
  }

  dayError(dayIndex: number): string | null {
    const d = this.hoursDays.at(dayIndex).value as {
      day: string;
      open: boolean;
      ranges: HoursRange[];
    };
    return d.open ? this.bookingError(d.day, d.ranges) : null;
  }

  addRange(dayIndex: number): void {
    const ranges = this.rangesOf(dayIndex);
    const last = ranges.at(ranges.length - 1)?.value as HoursRange | undefined;
    ranges.push(
      this.rangeGroup(last ? { start_time: last.end_time, end_time: '' } : DEFAULT_BOOKING_RANGE),
    );
    this.hoursForm!.markAsDirty();
  }

  removeRange(dayIndex: number, rangeIndex: number): void {
    const ranges = this.rangesOf(dayIndex);
    if (ranges.length > 1) {
      ranges.removeAt(rangeIndex);
      this.hoursForm!.markAsDirty();
    }
  }

  /** Unsaved edits in either form; one Save button covers both. */
  get hasUnsavedChanges(): boolean {
    return !!this.form?.dirty || !!this.hoursForm?.dirty;
  }

  /** Same as opening hours saves no reservation hours at all. */
  private bookingHoursGroups(): ServingHoursGroup[] {
    const v = this.hoursForm!.getRawValue() as {
      custom: boolean;
      days: { day: string; open: boolean; ranges: HoursRange[] }[];
    };
    if (!v.custom) {
      return [];
    }
    return groupByRanges(
      v.days
        .filter((d) => d.open)
        .map((d) => ({
          day: d.day,
          ranges: [...d.ranges].sort((a, b) => a.start_time.localeCompare(b.start_time)),
        })),
    );
  }

  private saveBookingHours(): Observable<ServingHoursDoc> {
    return this.restaurantService
      .saveServingHoursOfType('reservation_hours', this.bookingHoursGroups())
      .pipe(
        tap((doc) => {
          this.hoursForm!.markAsPristine();
          // As the profile page does after saving hours: republish, so the
          // book drops its cached hours and offers the new times.
          const current = this.restaurantService.currentRestaurantDetails$.value;
          if (current) {
            this.restaurantService.currentRestaurantDetails$.next({
              ...current,
              serving_hours: doc,
            });
          }
        }),
      );
  }

  get mode(): ReservationMode {
    return this.form.get('mode')!.value;
  }

  /** A row of a FormArray, typed for [formGroup] in the template. */
  asGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  get turnTimes(): FormArray {
    return this.form.get('turnTimes') as FormArray;
  }

  get showsTimed(): boolean {
    return ['timed', 'request', 'hybrid'].includes(this.mode);
  }

  get showsWaitlist(): boolean {
    return ['waitlist', 'hybrid'].includes(this.mode);
  }

  /**
   * Off takes no bookings and runs no queue, so nothing can reach a guest and
   * no outside channel can book: Guest notifications and Booking channels are
   * hidden (FNF-1176). Hidden only — the controls stay in the form, so their
   * saved values come back on switching mode and are still sent on save.
   */
  get showsGuestSettings(): boolean {
    return this.mode !== 'off';
  }

  /** Reminders are relative to a booking time; walk-ins in the queue have none. */
  get showsReminders(): boolean {
    return this.showsTimed;
  }

  /** What an SMS is actually used for in the selected mode. */
  get smsDescription(): string {
    switch (this.mode) {
      case 'waitlist':
        return '"Table ready" texts when a walk-in\'s table is free. Required for Google waitlists.';
      case 'hybrid':
        return 'Booking confirmations and changes, and "table ready" texts for the waitlist.';
      default:
        return 'Booking confirmations and changes.';
    }
  }

  setMode(mode: ReservationMode): void {
    this.form.get('mode')!.setValue(mode);
    this.form.markAsDirty();
  }

  addBand(): void {
    const last = this.turnTimes.at(this.turnTimes.length - 1)?.value;
    this.turnTimes.push(
      this.fb.group({
        maxParty: [
          Math.min(99, (last?.maxParty ?? 4) + 2),
          [Validators.required, Validators.min(1), Validators.max(99)],
        ],
        minutes: [
          (last?.minutes ?? 90) + 15,
          [Validators.required, Validators.min(15), Validators.max(360)],
        ],
      }),
    );
    this.form.markAsDirty();
  }

  removeBand(index: number): void {
    if (this.turnTimes.length > 1) {
      this.turnTimes.removeAt(index);
      this.form.markAsDirty();
    }
  }

  private get reminders(): number[] {
    return this.form.get('notifications.reminderMinutesBefore')!.value as number[];
  }

  reminderOn(minutes: number): boolean {
    return this.reminders.includes(minutes);
  }

  /**
   * At or over the cap. A setting saved before the cap may hold more than
   * MAX_REMINDERS; it is shown as saved and never trimmed, so the owner
   * deselects down to the cap before adding another.
   */
  get remindersAtCap(): boolean {
    return this.reminders.length >= MAX_REMINDERS;
  }

  /** Deselecting is always allowed; selecting only while under the cap. */
  reminderDisabled(minutes: number): boolean {
    return this.remindersAtCap && !this.reminderOn(minutes);
  }

  toggleReminder(minutes: number): void {
    if (this.reminderDisabled(minutes)) {
      return;
    }
    const ctrl = this.form.get('notifications.reminderMinutesBefore')!;
    const current = this.reminders;
    ctrl.setValue(
      current.includes(minutes)
        ? current.filter((m) => m !== minutes)
        : [...current, minutes].sort((a, b) => b - a),
    );
    this.form.markAsDirty();
  }

  save(): void {
    const saveSettings = this.form.dirty;
    const saveHours = !!this.hoursForm?.dirty;
    if (this.form.invalid || (saveHours && this.hoursForm!.invalid)) {
      this.form.markAllAsTouched();
      this.hoursForm?.markAllAsTouched();
      this.toastr.warning('Fix the highlighted fields first.', 'Check the form');
      return;
    }
    const settings = saveSettings ? this.settingsPayload() : null;
    if (settings && settings.party.min > settings.party.max) {
      this.toastr.warning('Minimum party cannot exceed the maximum.', 'Check the form');
      return;
    }
    const steps: Observable<unknown>[] = [];
    let savedMode: string | null = null;
    if (settings) {
      steps.push(
        this.reservationService.saveSettings(settings).pipe(
          tap((saved) => {
            this.version = saved.version;
            savedMode = saved.mode;
            this.form.markAsPristine();
          }),
        ),
      );
    }
    if (saveHours) {
      steps.push(this.saveBookingHours());
    }
    if (!steps.length) {
      return;
    }
    this.saving = true;
    this.spinner.show();
    concat(...steps)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.spinner.hide();
        }),
      )
      .subscribe({
        complete: () =>
          this.toastr.success(
            savedMode
              ? `Mode: ${savedMode}. Changes apply to new bookings.`
              : 'Changes apply to new bookings.',
            savedMode ? 'Reservation settings saved' : 'Booking hours saved',
          ),
        error: (err) => {
          const reason = this.errorMessage(err);
          this.toastr.error(
            savedMode ? `Settings saved, but the booking hours were not: ${reason}` : reason,
            'Error',
          );
        },
      });
  }

  private errorMessage(err: any): string {
    if (err instanceof Error) {
      return err.message;
    }
    const detail = err?.error?.detail ?? err?.error?.message;
    return typeof detail === 'string' ? detail : 'Unable to save settings.';
  }

  private settingsPayload(): ReservationSettings {
    const raw = this.form.getRawValue();
    const turnTimes = [...raw.turnTimes]
      .map((t: { maxParty: number; minutes: number }) => ({
        maxParty: Number(t.maxParty),
        minutes: Number(t.minutes),
      }))
      .sort((a, b) => a.maxParty - b.maxParty);
    return {
      ...raw,
      turnTimes,
      slotIntervalMinutes: Number(raw.slotIntervalMinutes),
      requestExpiryMinutes: Number(raw.requestExpiryMinutes),
      pacing: { maxCoversPerInterval: Number(raw.pacing.maxCoversPerInterval) },
      party: {
        min: Number(raw.party.min),
        max: Number(raw.party.max),
        autoConfirmUpTo: Number(raw.party.autoConfirmUpTo),
      },
      window: {
        minNoticeMinutes: Number(raw.window.minNoticeMinutes),
        maxDaysAhead: Number(raw.window.maxDaysAhead),
      },
      policy: {
        depositCents: Number(raw.policy.depositCents),
        cancelCutoffMinutes: Number(raw.policy.cancelCutoffMinutes),
        noShowGraceMinutes: Number(raw.policy.noShowGraceMinutes),
      },
      waitlist: {
        quoteStrategy: raw.waitlist.quoteStrategy,
        notifyTimeoutMinutes: Number(raw.waitlist.notifyTimeoutMinutes),
      },
      version: this.version,
    };
  }

  // ------------------------------------------------------------- tour

  startTour(): void {
    // Sections are shown per mode, so only keep the steps whose target is
    // actually on the page — a step pointing at nothing teaches nothing.
    this.tourSteps = this.buildTourSteps().filter((step) =>
      document.querySelector(`[data-tour="${step.target}"]`),
    );
    this.tourOpen = this.tourSteps.length > 0;
  }

  endTour(): void {
    this.tourOpen = false;
    try {
      localStorage.setItem(ReservationSettingsComponent.TOUR_SEEN_KEY, '1');
    } catch {
      /* private mode: the tour simply auto-starts again next time */
    }
  }

  private tourSeen(): boolean {
    try {
      return localStorage.getItem(ReservationSettingsComponent.TOUR_SEEN_KEY) === '1';
    } catch {
      return true;
    }
  }

  /** Steps are built on open so the copy reflects the mode now selected. */
  private buildTourSteps(): TourStep[] {
    const mode = (this.form?.get('mode')?.value ?? 'off') as ReservationMode;
    const queueOnly = mode === 'waitlist';
    const takesBookings = mode === 'timed' || mode === 'request' || mode === 'hybrid';
    return [
      {
        target: 'mode',
        title: 'Start with the mode',
        body: 'The mode decides everything else on this page. Off takes no bookings. Timed gives guests slots to book. Request makes every booking one you accept or decline. Walk-in queue is a same-day line with no slots. Timed + waitlist runs both over the same tables.',
      },
      {
        target: 'availability',
        title: 'How slots are built',
        body: takesBookings
          ? 'Slot interval sets how often a booking can start. Turn times decide how long a table is held, by party size. Pacing caps the covers the kitchen accepts in one interval. Party limits and the booking window bound what guests may ask for.'
          : 'These build the timed slots guests can book. They do nothing in the current mode, but they are kept for when you switch to timed or hybrid.',
      },
      {
        target: 'booking-hours',
        title: 'When guests can book',
        body: 'Same as opening hours lets guests book whenever the restaurant is open. Set booking hours to stop earlier, for example a last booking at 9 PM while the kitchen runs until 11. A day switched off takes no bookings. A booking still needs its full turn time inside these hours.',
      },
      {
        target: 'closed-dates',
        title: 'Holidays and special days',
        body: 'Close a date, such as a holiday or a private event, and it closes the whole restaurant: no orders, bookings or walk-ins. The same dates show under Restaurant Profile → Holidays & closed dates. Or give one date its own hours. Each one is saved as soon as you add it. Bookings already made for a closed date stay; you are told how many there are.',
      },
      {
        target: 'policy',
        title: 'Cancellations and no-shows',
        body: 'The cancel cutoff is how close to the booking a guest may still cancel themselves; inside it only you can. The no-show grace is how long after the start time you must wait before marking a party as a no-show.',
      },
      {
        target: 'waitlist',
        title: 'The walk-in queue',
        body: queueOnly
          ? 'Your mode runs on these. The quote strategy estimates the wait shown to guests, and the notify timeout is how long a table is held after you text "table ready" before the entry expires and the next party is up.'
          : 'Used when the queue is running. The quote strategy estimates the wait, and the notify timeout is how long a table is held after you text a guest before their spot expires.',
      },
      {
        target: 'notifications',
        title: 'What reaches the guest',
        body: queueOnly
          ? 'Whether walk-ins get a "table ready" text when their table is free. A guest with no phone number cannot be texted, so the dashboard will ask you to call them by name.'
          : 'Which channels confirmations and changes go out on, and up to two reminders before the booking. A guest with no phone number cannot be texted, so the dashboard will ask you to call them by name.',
      },
      {
        target: 'channels',
        title: 'Outside bookings',
        body: 'Where else bookings may come from. These are being built; when a channel is live, bookings from it appear in the book tagged with their source.',
      },
      {
        target: 'save',
        title: 'Saving',
        body: 'Changes apply to new bookings, never to ones already taken. If someone else saved while you were editing you will be told, so nobody quietly overwrites anyone.',
      },
      {
        target: 'book',
        title: 'Back to the floor',
        body: 'Open the book to work the day: bookings, the timeline and the queue. That page has its own tour behind the same question mark.',
      },
    ];
  }
}
